import { useCallback, useEffect, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import PortSlideCardDesktop from "./PortSlideCardDesktop"
import PortSlideCardMobile from "./PortSlideCardMobile"
import {
  CustomScrollbarThumb,
  CustomScrollbarTrack,
  StyledPortSlider,
  StyledPortSliderHeader,
  StyledSliderWrapper,
} from "./styled"
import { PortSliderProps } from "./types"

const PortSlider = ({
  ports,
  direction,
  swiperDirection,
  isDesktop,
  openInWazeLabel,
  publicTransportationLabel,
  parkingLabel,
  headerTitle,
  activePortIndex,
  onSwiperReady,
}: PortSliderProps) => {
  const reversedDirection = direction === "ltr" ? "rtl" : "ltr"
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const swiperRef = useRef<SwiperType | null>(null)
  const [thumbHeight, setThumbHeight] = useState(20)
  const [thumbTop, setThumbTop] = useState(0)
  const [hasScroll, setHasScroll] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartY = useRef(0)
  const dragStartScrollTop = useRef(0)

  const updateScrollbar = useCallback(() => {
    const container = scrollContainerRef.current?.querySelector(".swiper") as HTMLElement
    if (!container) return

    const { scrollHeight, clientHeight, scrollTop } = container
    const hasScrollableContent = scrollHeight > clientHeight

    setHasScroll(hasScrollableContent)

    if (hasScrollableContent) {
      const thumbHeightPercent = (clientHeight / scrollHeight) * 100
      const scrollableDistance = scrollHeight - clientHeight
      const thumbTopPercent =
        scrollableDistance > 0 ? (scrollTop / scrollableDistance) * (100 - thumbHeightPercent) : 0

      setThumbHeight(Math.max(thumbHeightPercent, 10))
      setThumbTop(thumbTopPercent)
    }
  }, [])

  const handleThumbMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const container = scrollContainerRef.current?.querySelector(".swiper") as HTMLElement
    if (!container) return

    setIsDragging(true)
    dragStartY.current = e.clientY
    dragStartScrollTop.current = container.scrollTop
  }, [])

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      const container = scrollContainerRef.current?.querySelector(".swiper") as HTMLElement
      const track = trackRef.current
      if (!container || !track) return

      const trackHeight = track.clientHeight
      const { scrollHeight, clientHeight } = container
      const scrollableDistance = scrollHeight - clientHeight

      const deltaY = e.clientY - dragStartY.current
      const scrollRatio = scrollableDistance / (trackHeight * (1 - thumbHeight / 100))
      const newScrollTop = dragStartScrollTop.current + deltaY * scrollRatio

      container.scrollTop = Math.max(0, Math.min(scrollableDistance, newScrollTop))
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isDragging, thumbHeight])

  const handleTrackClick = useCallback((e: React.MouseEvent) => {
    const container = scrollContainerRef.current?.querySelector(".swiper") as HTMLElement
    const track = trackRef.current
    if (!container || !track) return

    if ((e.target as HTMLElement).closest("[data-scrollbar-thumb]")) return

    const trackRect = track.getBoundingClientRect()
    const clickPosition = (e.clientY - trackRect.top) / trackRect.height
    const { scrollHeight, clientHeight } = container
    const scrollableDistance = scrollHeight - clientHeight

    container.scrollTop = clickPosition * scrollableDistance
  }, [])

  const handleSwiperProgress = useCallback(
    (swiper: SwiperType) => {
      if (!isDesktop) return

      const { progress } = swiper
      setThumbTop(progress * (100 - thumbHeight))
    },
    [isDesktop, thumbHeight],
  )

  const handleSwiperInit = useCallback(
    (swiper: SwiperType) => {
      swiperRef.current = swiper
      if (onSwiperReady) {
        onSwiperReady(swiper)
      }
      if (isDesktop) {
        const totalSlides = ports.length
        if (totalSlides > 1) {
          const thumbHeightPercent = Math.max(100 / totalSlides, 10)
          setThumbHeight(thumbHeightPercent)
          setHasScroll(true)
          setThumbTop(swiper.progress * (100 - thumbHeightPercent))
        }
      }
    },
    [isDesktop, ports.length, onSwiperReady],
  )

  useEffect(() => {
    const container = scrollContainerRef.current?.querySelector(".swiper") as HTMLElement
    if (!container || !isDesktop) return

    updateScrollbar()

    container.addEventListener("scroll", updateScrollbar)
    window.addEventListener("resize", updateScrollbar)

    const resizeObserver = new ResizeObserver(updateScrollbar)
    resizeObserver.observe(container)

    return () => {
      container.removeEventListener("scroll", updateScrollbar)
      window.removeEventListener("resize", updateScrollbar)
      resizeObserver.disconnect()
    }
  }, [isDesktop, updateScrollbar, ports])

  return (
    <StyledPortSlider>
      <StyledPortSliderHeader>{headerTitle}</StyledPortSliderHeader>
      <StyledSliderWrapper ref={scrollContainerRef}>
        <Swiper
          key={isDesktop ? "desktop" : "mobile"}
          dir={swiperDirection}
          slidesPerView={isDesktop ? "auto" : 1}
          direction={isDesktop ? "vertical" : "horizontal"}
          spaceBetween={isDesktop ? 0 : 16}
          autoHeight
          observer
          observeParents
          observeSlideChildren
          onSwiper={handleSwiperInit}
          onProgress={handleSwiperProgress}
          onSlideChange={(swiper) => handleSwiperProgress(swiper)}
        >
          {ports.map((port, index) => (
            <SwiperSlide key={`${port.portName}-${index}`} dir={direction}>
              {isDesktop ? (
                <PortSlideCardDesktop
                  port={port}
                  direction={reversedDirection}
                  openInWazeLabel={openInWazeLabel}
                  publicTransportationLabel={publicTransportationLabel}
                  parkingLabel={parkingLabel}
                  isActive={activePortIndex === index}
                />
              ) : (
                <PortSlideCardMobile
                  port={port}
                  direction={reversedDirection}
                  openInWazeLabel={openInWazeLabel}
                  publicTransportationLabel={publicTransportationLabel}
                  parkingLabel={parkingLabel}
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>
        {isDesktop && hasScroll && (
          <CustomScrollbarTrack ref={trackRef} onClick={handleTrackClick}>
            <CustomScrollbarThumb
              data-scrollbar-thumb
              $thumbHeight={thumbHeight}
              $thumbTop={thumbTop}
              $isDragging={isDragging}
              onMouseDown={handleThumbMouseDown}
            />
          </CustomScrollbarTrack>
        )}
      </StyledSliderWrapper>
    </StyledPortSlider>
  )
}

export default PortSlider
